import { WorkPage } from '../components/work-page.tsx';
import './schedule-pages.css';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCreationView } from '../hooks/use-creation-view.ts';
import { useTranslation } from 'react-i18next';
import { customerApi, quoteApi, scheduleApi, savedTeamsApi } from '../api/endpoints.ts';
import { api, ApiRequestError } from '../api/client.ts';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextArea, TextInput } from '../components/ui.tsx';
import { AlreadyPaidOnQuote } from '../components/payments/quote-payments.tsx';
import { useDocumentTitle, useOnlineStatus } from '../hooks/index.ts';
import {
  frequencyFillsQuoteSlot,
  isScheduledQuote,
  LOCKED_QUOTE_STATUSES,
  type QuoteStatus,
} from '@shared/constants/index.ts';
import {
  RuleFields,
  dayTimingOverrides,
  defaultRuleState,
  withFrequency,
  ruleMatchesPick,
  ruleToApiFields,
  ruleToRecurrence,
  ruleWithPick,
  firstVisitSelection,
} from '../components/schedule/rule-fields.tsx';
import { TeamSuggestions } from '../components/schedule/team-suggestions.tsx';
import { CleanerSuggestions } from '../components/schedule/cleaner-suggestions.tsx';
import { AssigneePicker } from '../components/schedule/assignee-picker.tsx';
import { AvailabilityPicker } from '../components/schedule/availability-picker.tsx';
import { SlotSuggestions } from '../components/schedule/slot-suggestions.tsx';
import {
  HorizonFields,
  defaultHorizon,
  horizonMaximumDate,
  horizonValidationKey,
} from '../components/schedule/horizon-fields.tsx';
import { BillingFields, type BillingFormState } from '../components/payments/billing-fields.tsx';
import { formatDuration, todayInTimezone } from '../components/schedule/format.ts';
import { useChecklistTemplateOptions } from '../components/schedule/use-checklist-template-options.ts';
import { useSession } from '../state/session.tsx';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import CustomerForm from '../forms/customer-form.tsx';
import { formatCents, perVisitCentsFromMonthlyTotal, suggestedVisitPriceCents } from '@shared/calculations/pricing.ts';
import {
  cadenceOfRule,
  MAX_VISIT_MINUTES,
  roundUpToQuarterHour,
  weekdaysForVisitCount,
} from '@shared/calculations/recurrence.ts';
import { SERIES_FREQUENCIES, type SeriesFrequency } from '@shared/constants/index.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { getDateLocale } from '../i18n/locale.ts';
import type { Customer } from '@shared/types/index.ts';

/**
 * Create a recurring (or one-time) schedule: pick the customer and property,
 * then the crew and the rule — with the crew's week drawn as a free/busy grid
 * so an open slot is a click, not a guess. Arriving from an accepted quote
 * pre-fills everything via query params, which is the "Schedule this
 * customer" handoff.
 */

/**
 * Schedule fields taken from the URL on a canceled-visit handoff.
 *
 * Shared by the first render and by later navigations that only change the
 * query string while this page stays mounted — as when someone already on
 * `/schedule/new` taps another freed-slot alert in the bell.
 */
/**
 * The open slot in a canceled-visit link, or null when this URL is some
 * other booking.
 *
 * Location is not part of the identity. The page removes it once that branch
 * is current, and a legacy visit never had one. Neither of those is a new slot.
 */
function freedSlotIdentity(params: URLSearchParams): string | null {
  if (!params.get('date') || !params.get('time') || params.get('customerId') || params.get('quoteId')) return null;
  return [params.get('date'), params.get('time'), params.get('duration'), params.get('frequency')].join('|');
}

function ruleFromScheduleParams(params: URLSearchParams, timezone: string | undefined, handoffMinutes: number | null) {
  const initial = defaultRuleState(todayInTimezone(timezone));
  const prefilledFrequency = params.get('frequency');
  const frequencyKnown =
    prefilledFrequency && (SERIES_FREQUENCIES as readonly string[]).includes(prefilledFrequency)
      ? (prefilledFrequency as SeriesFrequency)
      : null;
  const opened = frequencyKnown ? withFrequency(initial, frequencyKnown) : initial;
  const prefilledDate = params.get('date');
  if (prefilledDate && /^\d{4}-\d{2}-\d{2}$/.test(prefilledDate)) opened.startsOn = prefilledDate;
  const prefilledTime = params.get('time');
  if (prefilledTime && /^\d{2}:\d{2}$/.test(prefilledTime)) opened.startTimeLocal = prefilledTime;
  if (handoffMinutes !== null) {
    opened.durationMinutes = Math.min(MAX_VISIT_MINUTES, handoffMinutes);
  }
  return opened;
}

/** The page exists only on a plan that carries the calendar (plan-gate.tsx). */
export default function NewSeriesPage() {
  const { t } = useTranslation();
  if (!usePlanFeature('scheduling')) return <PlanRequiredPage feature="scheduling" title={t('schedule.page.title')} />;
  return <NewSeriesPageContent />;
}

function NewSeriesPageContent() {
  const { t } = useTranslation();
  useDocumentTitle(t('newSeries.documentTitle'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const prefilledCustomerId = params.get('customerId');
  const prefilledDuration = Number(params.get('duration'));
  /**
   * The crew size the prefilled duration was divided by — the notice's only
   * source, deliberately: it travels WITH the duration it explains, so the
   * pair stays consistent for a booker who lacks quotes:read and equally when
   * the quote's crew changes under this page while it opens. Without the note
   * a two-person slot gets staffed with one.
   */
  const prefilledCrew = Number(params.get('crew'));
  /**
   * The undivided labor minutes behind that duration. Only the rate-based
   * price fallback reads this: the hourly rate bills labor, so a quote that
   * can no longer be read (deleted, or a booker without quotes:read) must be
   * priced from the labor the crew delivers, not the wall-clock slice of it.
   */
  const prefilledLabor = Number(params.get('labour'));

  /**
   * The quote being booked, when the handoff carried one. State rather than
   * the URL parameter itself because it has to be droppable: the quote
   * belongs to the customer it was written for, and it must not follow a
   * change of customer onto somebody else's schedule — where it would both
   * price their visits from a stranger's quote and record that quote as this
   * work's provenance. The Worker checks only that the quote is this
   * location's, so nothing downstream would catch it.
   */
  const [quoteId, setQuoteId] = useState(params.get('quoteId'));

  const session = useSession();
  const { can, company, currentLocation } = session;
  const locations = session.locations ?? [];
  const switchLocation = session.switchLocation;

  /**
   * A canceled-visit alert links here with the branch in the query. The form
   * and its lookups stay closed until that branch is the current one. A
   * lookup that starts under the old branch can pick a customer the new
   * branch will not accept. A switch that fails leaves the form closed too.
   */
  const deepLinkLocationId = params.get('location');
  const knownLocation = Boolean(deepLinkLocationId && locations.some((entry) => entry.id === deepLinkLocationId));
  const [locationGate, setLocationGate] = useState<'pending' | 'ready' | 'blocked'>(() => {
    if (!deepLinkLocationId || deepLinkLocationId === currentLocation?.id) return 'ready';
    if (!locations.some((entry) => entry.id === deepLinkLocationId)) return 'blocked';
    return 'pending';
  });
  useEffect(() => {
    const target = deepLinkLocationId;
    if (!target || target === currentLocation?.id) {
      if (target && currentLocation?.id === target) {
        const next = new URLSearchParams(params);
        if (next.has('location')) {
          next.delete('location');
          setParams(next, { replace: true });
        }
      }
      setLocationGate('ready');
      return;
    }
    if (!knownLocation || typeof switchLocation !== 'function') {
      setLocationGate('blocked');
      return;
    }
    let active = true;
    setLocationGate('pending');
    void switchLocation(target)
      .then(() => {
        if (active) setLocationGate('ready');
      })
      .catch(() => {
        if (active) setLocationGate('blocked');
      });
    return () => {
      active = false;
    };
  }, [deepLinkLocationId, currentLocation?.id, knownLocation, switchLocation, params, setParams]);
  const branchReady = locationGate === 'ready';

  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  // "Change customer" has to actually stick: the prefill is applied once, and
  // dismissing it hands control to the ordinary picker instead of the cached
  // prefill query re-selecting the same customer on the next render.
  const [prefillDismissed, setPrefillDismissed] = useState(false);
  // Adding the customer here rather than leaving for the Customers page and
  // coming back to a form that has forgotten the rule, the crew and the price.
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [awaitingFirstProperty, setAwaitingFirstProperty] = useState(false);
  const [propertyId, setPropertyId] = useState(params.get('propertyId') ?? '');
  /**
   * The prefill as it lands in the form, rounded up to the quarter hour and
   * not clipped. Any positive prefill is real work: a crew can divide a short
   * job under the scheduler's 15-minute floor (20 labor minutes across two
   * people is 10), and dropping such a prefill would quietly book the
   * two-hour default instead — the round-up lands it on the floor, not
   * outside it. A job longer than 24 hours is capped at the longest visit the
   * calendar can store. The remainder stays unscheduled until the office
   * confirms that shorter visit or divides the work on the quote. Extra
   * weekdays would each receive the full checklist, so they are not used to
   * cover the overflow. Null when the handoff carried no duration.
   */
  const handoffMinutes =
    Number.isFinite(prefilledDuration) && prefilledDuration > 0 ? roundUpToQuarterHour(prefilledDuration) : null;
  const [rule, setRule] = useState(() =>
    ruleFromScheduleParams(params, currentLocation?.timezone ?? company?.timezone, handoffMinutes),
  );
  // The length the form opened with, so the labour fallback below can tell
  // an edited duration from any other edit (a new frequency is not one).
  const [openingDurationMinutes] = useState(rule.durationMinutes);
  const [horizon, setHorizon] = useState(defaultHorizon);
  const horizonSupported = company?.recurringHorizonMonths !== undefined;
  const online = useOnlineStatus();
  const horizonMaxDate = horizonMaximumDate(
    todayInTimezone(currentLocation?.timezone ?? company?.timezone),
    rule.startsOn,
  );
  const horizonInvalid =
    rule.frequency === 'one_time' || !horizonSupported
      ? null
      : horizonValidationKey(horizon, rule.startsOn, horizonMaxDate);
  // A weekday price box holding text that is not an amount: Create waits for it.
  const [dayPriceInvalid, setDayPriceInvalid] = useState(false);
  const [notes, setNotes] = useState('');
  const [billing, setBilling] = useState<BillingFormState>({ visitPriceCents: null });
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const savedTeams = useQuery({ queryKey: ['saved-teams'], queryFn: () => savedTeamsApi.list() });
  const checklistTemplates = useChecklistTemplateOptions();
  const defaultTeamCustomer = useRef<string | null>(null);
  const crewEditedCustomer = useRef<string | null>(null);
  useEffect(() => {
    if (
      !customer ||
      defaultTeamCustomer.current === customer.id ||
      crewEditedCustomer.current === customer.id ||
      !savedTeams.data
    )
      return;
    defaultTeamCustomer.current = customer.id;
    const entry = savedTeams.data.items.find((item) => item.id === customer.defaultTeamId && item.isActive);
    setTeamId(entry?.id ?? null);
    setAssignedUserIds(entry?.memberIds ?? []);
  }, [customer, savedTeams.data]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /**
   * The quote and customer the server refused a price for because the quote
   * was sent as a price range. It matters when the form could not read that
   * quote itself: the refusal is how it learns, and the next try books
   * without a price.
   */
  const [rangeRefused, setRangeRefused] = useState<{
    quoteId: string;
    customerId: string;
    frequency: string;
    intervalWeeks: number | null;
  } | null>(null);

  // Arriving with a customer already chosen (from a quote or customer page)
  // skips the picker entirely — until the prefill is dismissed or fails.
  const usePrefill = Boolean(prefilledCustomerId) && !prefillDismissed;
  const prefilledCustomer = useQuery({
    queryKey: ['customer', prefilledCustomerId],
    queryFn: () => customerApi.get(prefilledCustomerId!),
    enabled: branchReady && usePrefill && !customer,
  });
  // Not while somebody is typing a new customer into the form below: a
  // prefill that failed and then succeeded on a reconnect retry would
  // otherwise replace the half-filled form with the handoff's customer and
  // take everything typed into it with them.
  if (usePrefill && prefilledCustomer.data && !customer && !showCustomerForm) setCustomer(prefilledCustomer.data);

  const customers = useQuery({
    queryKey: ['customers', { search }],
    queryFn: () => customerApi.list({ q: search, pageSize: 20 }),
    enabled: branchReady && !customer && (!usePrefill || prefilledCustomer.isError),
  });

  const properties = useQuery({
    queryKey: ['customer-properties', customer?.id],
    queryFn: () => customerApi.properties(customer!.id),
    enabled: branchReady && Boolean(customer),
  });

  /**
   * Picks the property for a customer who was just added here.
   *
   * A new customer can arrive with a property already attached — the billing
   * address, saved as one by the tick box on the customer form — so the choice
   * waits for that list to load rather than leaving the dropdown on "no
   * specific property" for an address that was typed thirty seconds ago. With
   * nothing to pick the dropdown simply stays where it is: a property is
   * optional here, and there is no blank property form to open on this page.
   *
   * A failed lookup releases the wait rather than holding the button hostage
   * to it — the dropdown is still there, and so is the booking.
   */
  useEffect(() => {
    if (!awaitingFirstProperty) return;

    if (properties.isError) {
      setAwaitingFirstProperty(false);
      return;
    }

    const items = properties.data?.items;
    if (!items) return;

    setAwaitingFirstProperty(false);
    if (items[0]) setPropertyId(items[0].id);
  }, [awaitingFirstProperty, properties.data, properties.isError]);

  /**
   * The quote this booking came from, read for the one thing the handoff does
   * not carry: what it was sold for. Read here rather than passed along in the
   * URL because every door into this page has to price the visit the same way
   * — the quote list, the editor, a link someone kept — and only the record
   * itself is current at the moment of booking.
   */
  const quote = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => quoteApi.get(quoteId!),
    enabled: branchReady && Boolean(quoteId),
  });

  // The branch's currency, which its hourly rate below is denominated in.
  const currencyCode = session.currencyCode;
  // This branch's rate, not the head office's — a location may charge its own,
  // and the session resolves the override for exactly this kind of use.
  const hourlyRateCents = currentLocation?.defaultHourlyRateCents ?? company?.defaultHourlyRateCents ?? 0;

  /**
   * Whether the price in the box is the office's word rather than ours. Set by
   * the first edit and never unset: clearing the field is an answer too — bill
   * this outside the app — and re-suggesting over it would be an argument.
   */
  const [priceEdited, setPriceEdited] = useState(false);
  /**
   * Weekdays the office picked itself. Until then, a weekly quote sold at
   * more than one visit a week opens on the matching days (Monday and
   * Thursday, Monday/Wednesday/Friday, …) so one schedule holds the agreement.
   */
  const [weekdaysTouched, setWeekdaysTouched] = useState(false);
  /** Duration, days, or frequency edited by hand. The handoff stops rearranging the rule. */
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [acceptShortVisit, setAcceptShortVisit] = useState(false);

  /**
   * A canceled-visit alert carries only the open slot, not a customer. When
   * the bell sends someone here while this page is already open, the query
   * changes but the component does not remount — these fields have to follow
   * the new link.
   *
   * The ref starts as the slot this page opened on, or null when it opened as
   * an ordinary booking or a quote. Null is not "already applied": a legacy
   * visit has no location, so the first freed-slot link changes the URL only
   * once, and skipping that change would leave the previous customer, quote,
   * and notes in place. A page that opened on the slot already copied it into
   * the initial state, so the effect leaves that first paint alone.
   */
  const freedSlotKey = freedSlotIdentity(params);
  const appliedFreedSlot = useRef<string | null>(freedSlotKey);
  useEffect(() => {
    if (!branchReady || !freedSlotKey || appliedFreedSlot.current === freedSlotKey) return;
    appliedFreedSlot.current = freedSlotKey;
    const minutes =
      Number.isFinite(Number(params.get('duration'))) && Number(params.get('duration')) > 0
        ? roundUpToQuarterHour(Number(params.get('duration')))
        : null;
    setRule(ruleFromScheduleParams(params, currentLocation?.timezone ?? company?.timezone, minutes));
    setCustomer(null);
    setSearch('');
    setPrefillDismissed(false);
    setShowCustomerForm(false);
    setAwaitingFirstProperty(false);
    setPropertyId('');
    setQuoteId(null);
    setScheduleTouched(false);
    setWeekdaysTouched(false);
    setPriceEdited(false);
    setAcceptShortVisit(false);
    setAssignedUserIds([]);
    setNotes('');
    setHorizon(defaultHorizon());
    setBilling({ visitPriceCents: null });
    setError(null);
    setFieldErrors({});
  }, [branchReady, freedSlotKey, params, currentLocation?.timezone, company?.timezone]);

  /**
   * The quote as it applies to *this* booking: the record the handoff pointed
   * at, but only when it was written for the customer being booked.
   *
   * A handoff can name one customer and another customer's quote — a stale
   * link, an integration that built the URL from the wrong row — and nothing
   * downstream catches it. The create route checks the quote belongs to the
   * location and says so in as many words ("provenance, not authorization"),
   * and while it now declines to *default* a price from a mismatched quote,
   * it accepts whatever price is sent explicitly. This form sends one on every
   * submission, so the check has to live here too.
   */
  const quoteForCustomer = quote.data && customer && quote.data.customerId === customer.id ? quote.data : null;

  /*
    Two different questions about the same read, and they get different
    answers on purpose.

    `quotePending` is "nothing is known yet", and it governs what to suggest.
    `quoteRefreshing` is "a read is in flight", which includes a cached quote
    being refreshed behind an answer already on screen (30s stale time, and
    accepting a quote invalidates it by hand) — and it governs what may be
    submitted.

    Blanking the price box during a background refresh would make the figure
    flicker away and come back for no reason the reader can see, so the
    suggestion holds. But a price read a while ago is not one to commit: the
    form sends `visitPriceCents` explicitly, so the server takes it as given
    rather than recomputing, and a quote raised since would be billed at the
    old total for the life of the series. The box may show a figure it is
    re-checking; the button waits for the check.

    Both are gated on there being a quote at all, because a query that was
    never enabled reports itself pending forever — read as "waiting", that
    would wedge every booking made without one.
  */
  const quotePending = Boolean(quoteId) && quote.isPending;
  const quoteRefreshing = Boolean(quoteId) && quote.isFetching;

  /**
   * What to put in the price box before anybody types, and where it came from.
   * Null suggests nothing, leaving the field blank.
   */
  function suggestPrice(): {
    cents: number;
    source: 'quote' | 'monthlyQuote' | 'rate' | 'labor';
    laborMinutes?: number;
  } | null {
    if (quoteId) {
      // Still reading it — or still reading the customer whose quote it has to
      // be. A blank field for a moment beats the hourly estimate appearing and
      // then being overwritten by the quoted total: a price that changes by
      // itself is a price nobody trusts. The Create button waits for the quote
      // as well, so nothing can be booked out of the gap.
      if (quotePending || !customer) return null;
      if (
        quoteForCustomer &&
        LOCKED_QUOTE_STATUSES.includes(quoteForCustomer.status as QuoteStatus) &&
        // A one-off or non-weekly visit beside a several-visits-a-week quote
        // is not the schedule it sold, so the quote does not price it (#280).
        frequencyFillsQuoteSlot(rule.frequency, quoteForCustomer.visitsPerWeek, rule.intervalWeeks)
      ) {
        // A quote sent as a price range has no price yet: its total is only
        // the band's middle. Its visits are booked without one, and the final
        // price is set when the first is completed.
        if (quoteForCustomer.pricePromise === 'range') return null;
        // A total written in another currency is not a price in this one, and
        // converting it is not something this form could do honestly. Left for
        // someone to type.
        if (quoteForCustomer.currencyCode !== currencyCode) return null;
        /*
          A monthly quote's total buys the month, not the visit — this field
          prices one visit, and the form sends whatever is here explicitly, so
          the whole month suggested as-is would be billed on every visit for
          the life of the series. The visit's share is the month spread over
          the visits it covers: the exact 52/12 arithmetic the create route
          runs when no price is sent at all, so both doors price the same
          visit the same way. Its own source, because the hint owes the reader
          a different sentence about where this number came from.
        */
        const monthly = quoteForCustomer.billingBasis === 'monthly';
        const quotedCents = monthly
          ? perVisitCentsFromMonthlyTotal(quoteForCustomer.totalCents, currencyCode, {
              ...cadenceOfRule(ruleToRecurrence(rule), quoteForCustomer.visitsPerWeek),
              frequency: rule.frequency,
            })
          : quoteForCustomer.totalCents;
        const cents = suggestedVisitPriceCents({
          quotedTotalCents: quotedCents,
          durationMinutes: rule.durationMinutes,
          hourlyRateCents,
          currencyCode,
        });
        return cents === null ? null : { cents, source: monthly ? 'monthlyQuote' : 'quote' };
      }
      // Unsold, unreadable, or somebody else's quote. None of those price
      // this booking. The hourly rate below is the opening figure, the same
      // arithmetic a quote itself is priced with.
    }
    /*
      The rate is per LABOR hour, and a crewed handoff's duration is
      wall-clock — labor already divided by the crew — so pricing the
      duration would suggest a four-labor-hour job done by two people at
      half its worth, and the form saves that figure onto every visit in the
      series. The handoff carries the undivided labor minutes alongside for
      exactly this fallback; used only while the prefilled length still
      stands, because an edited duration is the office describing a new
      visit, and the labor figure explains only the one the quote produced.
    */
    const laborMinutes =
      rule.durationMinutes === openingDurationMinutes && Number.isFinite(prefilledLabor) && prefilledLabor > 0
        ? prefilledLabor
        : null;
    const cents = suggestedVisitPriceCents({
      quotedTotalCents: null,
      durationMinutes: laborMinutes ?? rule.durationMinutes,
      hourlyRateCents,
      currencyCode,
    });
    if (cents === null) return null;
    return laborMinutes !== null ? { cents, source: 'labor', laborMinutes } : { cents, source: 'rate' };
  }

  const suggestion = suggestPrice();

  /*
    Until it is typed in, the box IS the suggestion — including when there is
    no longer one to make. A suggestion that disappears has to take its number
    with it: shorten a visit until the estimate falls under the chargeable
    floor, or change customer away from the quote that priced it, and the
    figure left behind is one nothing on the page still stands behind. It would
    be saved as the price of every visit in the series.

    Adopted during render rather than from an effect, deliberately: an effect
    paints first and corrects afterwards, so the box would be seen holding the
    previous duration's estimate for a frame every time the suggestion moved.
    The write converges (it is skipped once the value already matches) and
    stops for good the moment the office types.
  */
  const suggestedPriceCents = suggestion?.cents ?? null;
  if (!priceEdited && billing.visitPriceCents !== suggestedPriceCents) {
    setBilling({ ...billing, visitPriceCents: suggestedPriceCents });
  }

  const soldQuote =
    quoteForCustomer && LOCKED_QUOTE_STATUSES.includes(quoteForCustomer.status as QuoteStatus)
      ? quoteForCustomer
      : null;
  const quotedVisits = soldQuote?.visitsPerWeek ?? 1;
  // A quote that still owes its old one-day series cannot be booked as
  // Monday/Wednesday/Friday on this form: the server refuses more than one
  // weekday. Leave the single default day selected.
  const oneWeekdayAtATime = (soldQuote?.seriesClaimCeiling ?? 1) > 1;
  // Several visits a week, each already short enough for one day, open on
  // the matching weekdays. A visit longer than 24 hours is not spread across
  // those days: every generated day is given the quote's full room list, so
  // extra days would repeat the job rather than partition it.
  if (
    !weekdaysTouched &&
    soldQuote &&
    rule.frequency === 'weekly' &&
    quotedVisits > 1 &&
    !oneWeekdayAtATime &&
    (handoffMinutes === null || handoffMinutes <= MAX_VISIT_MINUTES)
  ) {
    const nextDays = weekdaysForVisitCount(quotedVisits);
    const same =
      nextDays.length === rule.weekdays.length && nextDays.every((day, index) => day === rule.weekdays[index]);
    if (!same) {
      setRule({ ...rule, weekdays: nextDays, dayOfWeek: nextDays[0] ?? rule.dayOfWeek });
    }
  }

  /**
   * The shortest day on this rule, measured against one quoted visit.
   *
   * Adding a weekday does not reduce the gap. Each day is a full visit of
   * the quoted checklist, so a 40-hour apartment stays short until the
   * office accepts a 24-hour visit or splits the quote itself.
   */
  const requiredOnSite = handoffMinutes;
  const shortestVisitMinutes = (() => {
    const multi =
      rule.frequency === 'weekly' ||
      rule.frequency === 'biweekly' ||
      (rule.frequency === 'monthly' && rule.monthlyMode === 'nth_weekday');
    const days = multi && rule.weekdays.length > 0 ? rule.weekdays : [rule.dayOfWeek];
    return days.reduce((shortest, day) => {
      const override = rule.dayOverrides[String(day)]?.durationMinutes;
      const length = override && override >= 15 ? override : rule.durationMinutes;
      return Math.min(shortest, Math.min(MAX_VISIT_MINUTES, Math.max(0, length)));
    }, MAX_VISIT_MINUTES);
  })();
  const unscheduledMinutes =
    requiredOnSite !== null && shortestVisitMinutes < requiredOnSite ? requiredOnSite - shortestVisitMinutes : 0;

  /**
   * Whether this booking is the sold quote's and that quote was sent as a
   * price range. Its visits are booked without a price, which the server
   * insists on, so the box stays empty and locked, and says why. A quote the
   * form could not read counts once the server has refused a price for it.
   */
  const rangeBooking =
    (soldQuote?.pricePromise === 'range' &&
      frequencyFillsQuoteSlot(rule.frequency, soldQuote.visitsPerWeek, rule.intervalWeeks)) ||
    (rangeRefused !== null &&
      rangeRefused.quoteId === quoteId &&
      rangeRefused.customerId === customer?.id &&
      // Refused for this cadence: another one may not be priced by the quote
      // at all (#280), and the server says so again if it still is.
      rangeRefused.frequency === rule.frequency &&
      (rangeRefused.intervalWeeks ?? 1) === (rule.intervalWeeks ?? 1));

  /** Where a suggested price came from, said in the field's own hint. */
  const priceHint = rangeBooking
    ? t('quotes.schedule.rangeNote')
    : !suggestion || priceEdited
      ? undefined
      : suggestion.source === 'quote'
        ? t('newSeries.priceFromQuote', { number: quoteForCustomer?.quoteNumber ?? '' })
        : suggestion.source === 'monthlyQuote'
          ? // The monthly figure is quoted back with "/month" on it: this hint
            // is the only place the page shows that total, and a bare "$1,800"
            // next to a per-visit box would read as a contradiction.
            t('newSeries.priceFromMonthlyQuote', {
              number: quoteForCustomer?.quoteNumber ?? '',
              monthly: formatCents(quoteForCustomer?.totalCents ?? 0, currencyCode, getDateLocale()),
            })
          : suggestion.source === 'labor'
            ? // The labor figure, not the on-site duration: the sentence has
              // to name the number the arithmetic actually used.
              t('newSeries.priceFromRateLabor', {
                rate: formatCents(hourlyRateCents, currencyCode, getDateLocale()),
                labor: formatDuration(suggestion.laborMinutes ?? rule.durationMinutes),
              })
            : t('newSeries.priceFromRate', {
                rate: formatCents(hourlyRateCents, currencyCode, getDateLocale()),
                duration: formatDuration(rule.durationMinutes),
              });

  /**
   * A quote the server says is not there.
   *
   * It matters at submission, not just for the price: the create endpoint
   * refuses an unknown `quoteId` outright, so sending one the read already
   * 404'd would make this form unsubmittable — the handoff link outliving its
   * quote would leave the office unable to book the work at all. Only "not
   * found" counts. A refused read (no permission on quotes) or a failed one
   * says nothing about whether the quote exists, and dropping the link on that
   * evidence would quietly lose the booking's provenance.
   */
  const quoteIsGone = quote.isError && quote.error instanceof ApiRequestError && quote.error.status === 404;

  /**
   * The quote to file as this schedule's provenance, or null when there is
   * none to file: one the server says is gone, or one written for somebody
   * else. Decided on a finished read every time, because the button below
   * waits for that read.
   */
  const provenanceQuoteId =
    !quoteId || quoteIsGone || (quote.data && !quoteForCustomer) || (quoteForCustomer && !soldQuote) ? null : quoteId;

  /**
   * Whether the quote this booking was handed off with already has its
   * schedule. The create endpoint refuses a second one, so the form says so
   * up front and keeps its button down: a booking nothing can accept should
   * not be a form anybody can fill in. Extra days of a multi-visit contract
   * are added by editing that schedule, not by booking another.
   */
  // A non-weekly schedule beside a several-visits-a-week quote spends none of
  // its slots (#280), so a booked quote does not stop it.
  const quoteAlreadyScheduled = Boolean(
    quoteForCustomer &&
    isScheduledQuote(quoteForCustomer) &&
    frequencyFillsQuoteSlot(rule.frequency, quoteForCustomer.visitsPerWeek, rule.intervalWeeks),
  );

  const captureCreationView = useCreationView([customer?.id, propertyId, provenanceQuoteId]);
  // What the availability grid ghosts and checks: the schedule's real first
  // visit, the one the server checks before it books (see firstVisitSelection).
  const firstVisit = firstVisitSelection(rule, todayInTimezone(currentLocation?.timezone ?? company?.timezone));
  const create = useMutation({
    networkMode: 'always',
    mutationFn: ({
      body,
      isCurrent,
    }: {
      body: Record<string, unknown>;
      isCurrent: () => boolean;
      // The cadence this request was sent with: a refusal belongs to it, not
      // to whatever the form shows by the time the answer arrives (#280).
      frequency: string;
      intervalWeeks: number | null;
    }) => {
      if (!isCurrent()) throw new Error('The schedule creation view changed before submission.');
      return scheduleApi.createSeries(body, { isCurrent });
    },
    onSuccess: (series, attempt) => {
      // The server kept the schedule even when Cancel/Back dropped navigation.
      // Refresh calendar/list caches so the office does not create a duplicate.
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['series'] });
      // The booking just claimed the quote server-side, so every cached copy
      // of it is a fact out of date: the list would go on offering "Schedule"
      // for work now on the calendar, and badging it Accepted rather than
      // Scheduled, until the cache happened to go stale. Only when a quote
      // was actually filed as this schedule's provenance — a booking made
      // without one changed no quote.
      if (attempt.body.quoteId) invalidateQuoteViews(queryClient);
      if (attempt.isCurrent()) navigate(`/schedule/series/${series.id}`, { replace: true });
    },
    onError: (caught, attempt) => {
      if (!attempt.isCurrent()) return;
      if (caught instanceof ApiRequestError) {
        if (caught.code === 'RANGE_QUOTE_UNPRICED' && quoteId && customer) {
          setRangeRefused({
            quoteId,
            customerId: customer.id,
            frequency: attempt.frequency,
            intervalWeeks: attempt.intervalWeeks,
          });
        }
        setFieldErrors(caught.fields);
        setError(Object.keys(caught.fields).length > 0 ? null : caught.message);
      } else {
        setError(t('newSeries.createError'));
      }
    },
  });

  if (locationGate !== 'ready') {
    return (
      <WorkPage title={t('newSeries.title')} description={t('newSeries.description')}>
        {locationGate === 'blocked' ? (
          <Alert tone="error">{t('newSeries.locationUnavailable')}</Alert>
        ) : (
          <LoadingBlock label={t('newSeries.openingLocation')} />
        )}
      </WorkPage>
    );
  }

  return (
    <WorkPage title={t('newSeries.title')} description={t('newSeries.description')}>
      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {/* Arrived here from a link that outlived its quote's booking — a tab
          left open, a bookmark, a second window. The create endpoint refuses
          it anyway, but finding that out after filling the whole form in is a
          worse way to learn it than being told now, next to the schedule the
          quote already has. */}
      {quoteAlreadyScheduled && quoteForCustomer?.scheduledSeriesId ? (
        <div className="mb-6">
          <Alert tone="warning">
            {t('newSeries.quoteAlreadyScheduled', { number: quoteForCustomer.quoteNumber })}{' '}
            <Link
              to={`/schedule/series/${quoteForCustomer.scheduledSeriesId}`}
              className="text-[color:var(--color-brand)] hover:underline"
            >
              {t('newSeries.quoteAlreadyScheduledLink')}
            </Link>
          </Alert>
        </div>
      ) : null}

      {quoteForCustomer && !soldQuote ? (
        <div className="mb-6">
          <Alert tone="info">{t('newSeries.quoteNotAccepted', { number: quoteForCustomer.quoteNumber })}</Alert>
        </div>
      ) : null}

      {/* ---- Step 1: customer ------------------------------------------- */}
      <section className="card mb-6" aria-labelledby="schedule-customer-heading">
        <h2 id="schedule-customer-heading" className="mb-4 text-lg font-semibold">
          {t('newSeries.customerHeading')}
        </h2>

        {customer ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">{customer.displayName}</p>
              <p className="text-sm text-[color:var(--color-muted)]">
                {[customer.email, customer.phone].filter(Boolean).join(' · ') || t('newSeries.noContactDetails')}
              </p>
              {!customer.email ? (
                <p className="mt-1 text-sm text-[color:var(--color-warning)]">{t('newSeries.noEmailWarning')}</p>
              ) : null}
            </div>
            <Button
              onClick={() => {
                setPrefillDismissed(true);
                setCustomer(null);
                setPropertyId('');
                // A property still being chosen for the customer being left
                // must not land on whoever is picked next.
                setAwaitingFirstProperty(false);
                // Nor does a refusal earned against them — "that property is
                // not this customer's" belongs to the customer being left.
                setError(null);
                setFieldErrors({});
                // The quote stays with its own customer.
                setQuoteId(null);
              }}
            >
              {t('newSeries.changeCustomer')}
            </Button>
          </div>
        ) : usePrefill && prefilledCustomer.isLoading ? (
          <LoadingBlock label={t('newSeries.loadingCustomer')} />
        ) : showCustomerForm ? (
          <CustomerForm
            onSaved={(created) => {
              setCustomer(created);
              setShowCustomerForm(false);
              // A refusal earned by the customer who was here before —
              // "propertyId is not this customer's" most of all — says
              // nothing about this one, and would be shown against the
              // property just picked for them.
              setError(null);
              setFieldErrors({});
              // Whoever was just typed in is the answer to step 1, so a
              // prefill still in flight must not take the choice back.
              setPrefillDismissed(true);
              // Neither a quote nor a property carried in for somebody else
              // belongs to a customer who did not exist a moment ago.
              setQuoteId(null);
              setPropertyId('');
              setAwaitingFirstProperty(true);
            }}
            onCancel={() => setShowCustomerForm(false)}
          />
        ) : (
          <>
            <TextInput
              label={t('newSeries.findCustomer')}
              placeholder={t('newSeries.findCustomerPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {customers.isLoading ? <LoadingBlock label={t('newSeries.searching')} /> : null}
            {customers.data && customers.data.items.length > 0 ? (
              <ul className="divide-y divide-[color:var(--color-line)]">
                {customers.data.items.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                    <div>
                      <p className="font-medium">{row.displayName}</p>
                      <p className="text-sm text-[color:var(--color-muted)]">
                        {[row.email, row.phone].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setCustomer(row);
                        // The picker is reachable alongside a handoff too —
                        // while the prefilled customer is still loading, or
                        // when that lookup failed — so a quote or property
                        // carried in for somebody else must not ride along
                        // onto the customer chosen here.
                        if (row.id !== prefilledCustomerId) {
                          setQuoteId(null);
                          setPropertyId('');
                        }
                      }}
                    >
                      {t('newSeries.choose')}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : customers.data ? (
              <p className="text-[color:var(--color-muted)]">{t('newSeries.noCustomersMatch')}</p>
            ) : null}

            {/* Adding a customer is a customer write, and the schedule is
                reachable by roles that have no such permission. Offering the
                button to them would walk somebody through the whole form only
                to end on a 403 — the same gate the Customers page puts on it. */}
            {can('customers:write') ? (
              <Button variant="primary" className="mt-4" onClick={() => setShowCustomerForm(true)}>
                {t('newSeries.addNewCustomer')}
              </Button>
            ) : null}
          </>
        )}
      </section>

      {/* ---- Step 2: where and when -------------------------------------- */}
      {customer ? (
        <section className="card mb-6" aria-labelledby="schedule-rule-heading">
          <h2 id="schedule-rule-heading" className="mb-4 text-lg font-semibold">
            {t('newSeries.whereWhenHeading')}
          </h2>

          {/* Held closed while a new customer's addresses are being read.
              The list is empty until they land, so its only option is the one
              already selected — and a native select fires no change event for
              re-picking that, meaning "no specific property" could be chosen
              and then silently undone by the address arriving a moment later.
              One request long, and the Create button waits with it. */}
          <Select
            label={t('newSeries.propertyLabel')}
            hint={awaitingFirstProperty ? t('newSeries.readingProperties') : t('newSeries.propertyHint')}
            disabled={awaitingFirstProperty}
            value={propertyId}
            error={fieldErrors.propertyId}
            options={[
              { value: '', label: t('newSeries.noSpecificProperty') },
              ...(properties.data?.items ?? []).map((property) => ({
                value: property.id,
                label:
                  property.name ??
                  [property.addressLine1, property.city].filter(Boolean).join(', ') ??
                  t('newSeries.unnamedProperty'),
              })),
            ]}
            onChange={(event) => {
              setPropertyId(event.target.value);
              // An answer given here IS the answer. Reachable in the single
              // render between the address list landing and the effect below
              // applying its default — narrow, but the alternative is a
              // choice overwritten by one nobody made.
              setAwaitingFirstProperty(false);
            }}
          />

          <AssigneePicker
            disabled={create.isPending}
            value={assignedUserIds}
            teamId={teamId}
            onTeamChange={(id, members) => {
              crewEditedCustomer.current = customer?.id ?? null;
              setTeamId(id);
              setAssignedUserIds(members);
            }}
            onChange={(members) => {
              crewEditedCustomer.current = customer?.id ?? null;
              setAssignedUserIds(members);
            }}
            error={fieldErrors.assignedUserIds ?? fieldErrors.assignedUserId}
          />

          {!rule.endsOn &&
          Object.keys(rule.dayOverrides).length === 0 &&
          !rule.preservedSlots?.length &&
          (rule.frequency === 'one_time' ||
            (rule.weekdays.length <= 1 &&
              (rule.frequency === 'monthly'
                ? rule.nthWeeks.length <= 1
                : rule.intervalWeeks === (rule.frequency === 'biweekly' ? 2 : 1)))) ? (
            <div className="schedule-suggestions">
              {/* Auto-scheduling: a sold quote being booked gets the top three
                  cleaner-and-slot proposals. Picking one fills the form; the
                  dispatcher still saves the booking (never auto-assigned). */}
              {quoteId && (
                <CleanerSuggestions
                  quoteId={quoteId}
                  propertyId={propertyId || undefined}
                  enabled={!create.isPending}
                  from={rule.startsOn}
                  durationMinutes={rule.durationMinutes}
                  frequency={rule.frequency}
                  onPick={(pick) => {
                    crewEditedCustomer.current = customer?.id ?? null;
                    setTeamId(null);
                    setAssignedUserIds([pick.userId]);
                    setRule((current) => ruleWithPick(current, pick.startsOn, pick.startTimeLocal, pick.nthWeek));
                  }}
                />
              )}
              <TeamSuggestions
                enabled={!create.isPending}
                from={rule.startsOn}
                durationMinutes={rule.durationMinutes}
                frequency={rule.frequency}
                onPick={(id, members, pick) => {
                  crewEditedCustomer.current = customer?.id ?? null;
                  setTeamId(id);
                  setAssignedUserIds(members);
                  setRule((current) => ruleWithPick(current, pick.startsOn, pick.startTimeLocal, pick.nthWeek));
                }}
              />
            </div>
          ) : (
            <p className="field-hint">{t('planning.advancedSuggestionHint')}</p>
          )}
          {/* The handoff's duration is wall-clock time for the QUOTED crew.
              Said beside the people being picked, because the two only add up
              together: a two-hour slot divided for two cleaners and staffed
              with one is a four-hour visit on a two-hour calendar block.
              Only while the prefilled length still stands — once the office
              edits the duration, the claim would be about a number the quote
              never produced. And the handoff's own crew, deliberately not the
              freshly fetched quote's: the claim is about the duration that
              was divided at handoff time, and if another editor changes the
              quote's crew while this page opens, pairing the stale length
              with the new headcount would tell the booker to staff a slot
              the arithmetic never produced. */}
          {!scheduleTouched && Number.isFinite(prefilledCrew) && prefilledCrew > 1 ? (
            <div className="mb-4">
              <Alert tone="info">{t('newSeries.crewSizeNotice', { count: prefilledCrew })}</Alert>
            </div>
          ) : null}

          {unscheduledMinutes > 0 ? (
            <div className="mb-4">
              <Alert tone="warning">
                {t('newSeries.durationDoesNotFit', {
                  duration: formatDuration(requiredOnSite ?? 0),
                  short: formatDuration(unscheduledMinutes),
                })}
              </Alert>
              <Checkbox
                label={t('newSeries.bookShorterVisit')}
                checked={acceptShortVisit}
                onChange={(event) => setAcceptShortVisit(event.target.checked)}
              />
            </div>
          ) : null}

          {/* The rule, and only then the calendar.
              How often, what time and how long are what the quick picks and
              the grid are computed FROM — a two-hour weekly job and a
              forty-minute monthly one do not have the same open slots — so
              they are asked first and the times below answer a question that
              has already been put. It matters most on a phone, where a week
              grid is a screenful: fields underneath it are fields nobody
              scrolls back up from. Picking still writes back here (and a
              repeating rule follows the picked weekday); the grid marks the
              chosen hour, so the tap is acknowledged where it happened. */}
          <RuleFields
            rule={rule}
            fieldErrors={fieldErrors}
            // No weekday prices on a range quote: its visits are booked
            // unpriced until the final price is set at the first one.
            {...(rangeBooking ? {} : { currencyCode })}
            checklistTemplates={checklistTemplates}
            onDayPriceInvalidChange={setDayPriceInvalid}
            onChange={(next) => {
              // A weekday's price or checklist is not part of when the visits
              // happen, so it leaves the calendar suggestions alone.
              const patternChanged =
                next.weekdays.join(',') !== rule.weekdays.join(',') ||
                next.intervalWeeks !== rule.intervalWeeks ||
                next.nthWeeks.join(',') !== rule.nthWeeks.join(',') ||
                JSON.stringify(dayTimingOverrides(next.dayOverrides)) !==
                  JSON.stringify(dayTimingOverrides(rule.dayOverrides));
              if (patternChanged) setWeekdaysTouched(true);
              if (
                patternChanged ||
                next.durationMinutes !== rule.durationMinutes ||
                next.frequency !== rule.frequency
              ) {
                setScheduleTouched(true);
              }
              setRule(next);
            }}
          />

          {rule.frequency !== 'one_time' && horizonSupported ? (
            <HorizonFields
              value={horizon}
              onChange={setHorizon}
              companyMonths={company?.recurringHorizonMonths ?? 3}
              startsOn={rule.startsOn}
              maxDate={horizonMaxDate}
              fieldErrors={fieldErrors}
            />
          ) : null}
          {rule.frequency !== 'one_time' && !horizonSupported ? (
            <div className="mb-4">
              <Alert tone="info">{t('recurringHorizon.legacyBooking')}</Alert>
            </div>
          ) : null}
          {horizonInvalid ? <p className="mb-4 text-sm text-[color:var(--color-danger)]">{t(horizonInvalid)}</p> : null}
          {!online ? <Alert tone="info">{t('recurringHorizon.offline')}</Alert> : null}

          <SlotSuggestions
            teamId={teamId}
            userIds={assignedUserIds}
            durationMinutes={rule.durationMinutes}
            frequency={rule.frequency}
            isSelected={(suggestion) =>
              ruleMatchesPick(rule, suggestion.startsOn, suggestion.startTimeLocal, suggestion.nthWeek)
            }
            onPick={(suggestion) =>
              setRule((current) =>
                ruleWithPick(current, suggestion.startsOn, suggestion.startTimeLocal, suggestion.nthWeek),
              )
            }
          />
          <AvailabilityPicker
            teamId={teamId}
            userIds={assignedUserIds}
            durationMinutes={firstVisit?.durationMinutes ?? rule.durationMinutes}
            selected={firstVisit ? { date: firstVisit.date, time: firstVisit.time } : null}
            onPick={(date, time) => setRule((current) => ruleWithPick(current, date, time))}
          />

          <BillingFields
            billing={rangeBooking ? { ...billing, visitPriceCents: null } : billing}
            disabled={rangeBooking}
            onChange={(next) => {
              if (next.visitPriceCents !== billing.visitPriceCents) setPriceEdited(true);
              setBilling(next);
            }}
            fieldErrors={fieldErrors}
            {...(priceHint ? { priceHint } : {})}
          />
          {/* Money already taken on the quote stays on it, so the price set
              here is the office's call with that figure in front of them. */}
          {provenanceQuoteId ? <AlreadyPaidOnQuote quoteId={provenanceQuoteId} /> : null}

          <TextArea
            label={t('newSeries.internalNotes')}
            hint={t('newSeries.internalNotesHint')}
            tone="internal"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            {/* Busy until the quote has been read, not only while saving.
                The customer request and the quote request race, and the
                customer's is the one that reveals this section — booking in
                the gap would create a schedule linked to a quote and priced at
                nothing, not because anyone decided these visits were billed
                elsewhere but because the form had not finished saying what
                they cost. A quote being re-read behind a figure already on
                screen waits too: what is submitted is what the form last
                stated, and a total raised since would be billed at the old
                one for the life of the series. Only a booking made from a
                quote waits at all, and only for as long as one read takes. */}
            {/* Busy also covers the moment the property is still being decided
                for a customer who was just added here: a click in that gap
                would book against no property at all and quietly drop the one
                their billing address had already become. */}
            {/* Disabled, not merely warned about, when the quote behind this
                handoff has already been booked: the server refuses that
                create outright, and letting somebody fill the whole form in
                to reach a button that cannot work is the booking this change
                exists to stop offering. `disabled` rather than `busy` — the
                form is not waiting on anything, it has nowhere to go. */}
            <Button
              variant="primary"
              busy={create.isPending || quoteRefreshing || awaitingFirstProperty}
              disabled={
                quoteAlreadyScheduled ||
                (unscheduledMinutes > 0 && !acceptShortVisit) ||
                Boolean(horizonInvalid) ||
                dayPriceInvalid ||
                !online
              }
              onClick={() =>
                create.mutate({
                  body: {
                    customerId: customer!.id,
                    propertyId: propertyId || null,
                    quoteId: provenanceQuoteId,
                    ...(crewEditedCustomer.current === customer?.id || teamId ? { assignedUserIds, teamId } : {}),
                    internalNotes: notes || null,
                    visitPriceCents: rangeBooking ? null : billing.visitPriceCents,
                    // The first visit must be clear; later conflicts can be
                    // skipped and surfaced on the confirmed series.
                    allowOccurrenceConflicts: true,
                    ...ruleToApiFields(rule),
                    ...(rule.frequency === 'one_time' || !horizonSupported ? {} : horizon),
                  },
                  isCurrent: captureCreationView(),
                  frequency: rule.frequency,
                  intervalWeeks: rule.intervalWeeks,
                })
              }
            >
              {t('newSeries.createSchedule')}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/schedule')}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      ) : null}
    </WorkPage>
  );
}
